import { useQuery } from "@tanstack/react-query";
import {
  fetchDexExecCostBuckets,
  fetchEffectiveSpreadBuckets,
  fetchQuotedSpreadBuckets,
  fetchRealizedSpreadBuckets,
  type BucketParams,
} from "../api/client.ts";
import type { BoxRow } from "../lib/boxwhisker.ts";

const POLL_MS = 15_000;

export type SpreadMetric = "effective" | "realized" | "quoted" | "exec_cost";

export function useSpreadBuckets(metric: SpreadMetric, p: BucketParams) {
  const query = useQuery({
    queryKey: [
      "spread-buckets",
      metric,
      p.clientId,
      p.venue,
      p.pair,
      p.lookbackMinutes,
      p.bucketSeconds,
    ],
    queryFn: async (): Promise<{ rows: BoxRow[]; count: number }> => {
      if (metric === "quoted") {
        const r = await fetchQuotedSpreadBuckets(p);
        return {
          count: r.count,
          rows: r.buckets.map((b) => ({
            bucket: b.bucket,
            median_bps: b.median_bps,
            p25_bps: b.p25_bps,
            p75_bps: b.p75_bps,
            p05_bps: b.p05_bps,
            p95_bps: b.p95_bps,
            min_bps: b.min_bps,
            max_bps: b.max_bps,
            cov: b.two_sided_quote_count,
            total: b.total_quote_count,
          })),
        };
      }
      if (metric === "exec_cost") {
        const er = await fetchDexExecCostBuckets(p);
        return {
          count: er.length,
          rows: er.map((b) => ({
            bucket: b.bucket,
            median_bps: b.median_bps,
            p25_bps: b.p25_bps, p75_bps: b.p75_bps,
            p05_bps: b.p05_bps, p95_bps: b.p95_bps,
            min_bps: b.min_bps, max_bps: b.max_bps,
            cov: b.cov, total: b.total,
          })),
        };
      }
      const r =
        metric === "realized"
          ? await fetchRealizedSpreadBuckets(p)
          : await fetchEffectiveSpreadBuckets(p);
      return {
        count: r.count,
        rows: r.buckets.map((b) => ({
          bucket: b.bucket,
          median_bps: b.median_bps,
          p25_bps: b.p25_bps,
          p75_bps: b.p75_bps,
          p05_bps: b.p05_bps,
          p95_bps: b.p95_bps,
          min_bps: b.min_bps,
          max_bps: b.max_bps,
          cov: b.fresh_mid_trade_count,
          total: b.total_trade_count,
        })),
      };
    },
    refetchInterval: POLL_MS,
    staleTime: POLL_MS,
  });
  return {
    ...query,
    rows: query.data?.rows ?? [],
    count: query.data?.count ?? 0,
  };
}
